/**
 * Sprint V101.f (So 20.09.2026) — Post-Signup Inbox-Hinweis-Karte.
 *
 * Wird direkt nach dem erfolgreichen KT-DOI-Signin auf der Regpage
 * gerendert. Zeigt provider-adaptive Anleitung (Gmail / Outlook / GMX
 * usw.), wo die Bestätigungs-Mail zu finden ist. Erhöht Confirm-Rate,
 * ohne Deliverability-Root-Cause zu benötigen.
 *
 * Absender + Betreff sind Prod-Fakt (KlickTipp-Konfiguration Andreas):
 *   From: Andreas (Flex4Success) <info@effektiv-kommunizieren.de>
 *   Subject: „Dein Code wartet auf dich."
 */
import React from "react";
import { Mail, ExternalLink, FolderSearch, AlertTriangle, ArrowRight } from "lucide-react";
import { getEmailProviderHint } from "@/lib/emailProviderHint";

const SENDER_EMAIL = "info@effektiv-kommunizieren.de";
const SENDER_NAME = "Andreas (Flex4Success)";
const SUBJECT = "Dein Code wartet auf dich.";

export default function PostSignupInboxHint({ email }) {
  const hint = getEmailProviderHint(email);
  const showLabel = !!hint.label;
  const heading = showLabel
    ? `So findest du die Bestätigungs-Mail in ${hint.label}`
    : "So findest du die Bestätigungs-Mail";

  return (
    <div
      data-testid="post-signup-inbox-hint"
      data-provider={hint.providerKey}
      className="rounded-2xl border border-[#F3651D]/30 bg-gradient-to-br from-[#F3651D]/5 to-transparent p-5 sm:p-6"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#F3651D]/10 border border-[#F3651D]/30 flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5 text-[#F3651D]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-widest text-[#F3651D]">
            Nächster Schritt
          </p>
          <h3 className="mt-1 text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white leading-tight">
            {heading}
          </h3>
        </div>
      </div>

      {/* Absender-/Betreff-Info */}
      <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 text-sm">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Absender</div>
        <div className="mt-0.5 text-slate-900 dark:text-white font-semibold">
          {SENDER_NAME}
        </div>
        <div className="text-slate-500 font-mono text-xs">{SENDER_EMAIL}</div>
        <div className="mt-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Betreff</div>
        <div className="mt-0.5 text-slate-900 dark:text-white font-semibold">
          „{SUBJECT}"
        </div>
      </div>

      {/* Wo suchen? */}
      <div className="mt-4">
        <p className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
          <FolderSearch className="w-4 h-4 text-slate-500" />
          Wo du kurz reinschauen solltest
        </p>
        <ul className="mt-2 space-y-1.5">
          {hint.locations.map((loc, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <ArrowRight className="w-3.5 h-3.5 mt-0.5 text-[#F3651D] flex-shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: loc }} />
            </li>
          ))}
        </ul>
      </div>

      {/* Tipp */}
      <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-3 py-2.5 text-xs text-amber-900 dark:text-amber-100 flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span dangerouslySetInnerHTML={{ __html: hint.tip }} />
      </div>

      {/* Deeplink (falls verfügbar) */}
      {hint.deeplink && (
        <div className="mt-4">
          <a
            href={hint.deeplink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="post-signup-inbox-hint-deeplink"
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold px-4 py-2.5 text-sm hover:opacity-90 transition-opacity"
          >
            <ExternalLink className="w-4 h-4" />
            {hint.deeplinkLabel} <span className="text-slate-300 dark:text-slate-500">↗</span>
          </a>
          {/* Sprint V101.f · Client-Hinweis — der Button öffnet Webmail.
              Wer einen Desktop-Client (Thunderbird / Apple Mail / Outlook
              Desktop) nutzt, findet die Mail dort auch — Hinweis darauf,
              damit der Klick nicht verwirrt. */}
          <p
            className="mt-2 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400 max-w-md"
            data-testid="post-signup-inbox-hint-client-note"
          >
            Der Button öffnet die Webmail im Browser. Nutzt du einen anderen
            Mail-Client (z. B. Thunderbird, Apple Mail, Outlook Desktop)?
            Such dort einfach nach dem Absender <span className="font-semibold text-slate-700 dark:text-slate-300">„Andreas (Flex4Success)"</span> — die Mail ist da.
          </p>
        </div>
      )}
    </div>
  );
}
